"use client";

import React, { useEffect, useRef, useState } from "react";
import { ICON_BASE_PATH } from "@/app/utils/config";

import * as pdfjsLib from "pdfjs-dist";
import GxIconButton from "@/app/components/common/actions/buttons/GxIconButton";
import GxDialog from "@/app/models/dialogs/GxDialog";

// pdf.js needs a worker script — pulling it from a CDN matching the installed version
// avoids bundler-specific worker config (webpack/vite/etc.)
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();
type FileKind = "pdf" | "image" | "auto";

interface FileCardProps {
  fileTitle: string;
  fileName: string;
  /** Direct URL to the file */
  fileUrl?: string;
  /** Base64 data (with or without data: prefix) */
  base64Output?: string;
  /** Local/server file path, used as a fallback src */
  filePath?: string;
  /** Force a file kind, or let it infer from the extension in fileName */
  fileType?: FileKind;
  downloading?: boolean;
  /** Called when the download icon is clicked; if omitted, falls back to a default <a> download */
  onDownload?: () => void;
  /** Called when the delete icon is clicked. Delete button only renders if this is passed. */
  onDelete?: () => void;
  /** NEW: called when the "x" remove button is clicked - meant for
   * clearing an existing/already-on-file document so the consumer can
   * swap back to its upload control. Separate from onDelete (which keeps
   * its own existing behavior/icon). Button only renders if this is
   * passed. */
  onRemove?: () => void;
  /** Custom JSX shown on the right side (e.g. approval status). Only renders if passed. */
  statusContent?: React.ReactNode;
  className?: string;
}

const getFileKind = (fileName: string, fileType?: FileKind): "pdf" | "image" => {
  if (fileType && fileType !== "auto") return fileType;
  const ext = fileName.split(".").pop()?.toLowerCase() || "";
  if (["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext)) {
    return "image";
  }
  return "pdf";
};

const normalizeSrc = (base64Output?: string) => {
  if (!base64Output) return undefined;
  if (base64Output.startsWith("data:") || base64Output.startsWith("http")) {
    return base64Output;
  }
  return `data:application/pdf;base64,${base64Output}`;
};

const PdfPreview: React.FC<{ src: string }> = ({ src }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const renderPdf = async () => {
      try {
        const loadingTask = pdfjsLib.getDocument(src);
        const pdf = await loadingTask.promise;
        if (cancelled || !containerRef.current) return;

        containerRef.current.innerHTML = "";

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale: 1.4 });

          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.className = "gx-pdf-page-canvas";

          const context = canvas.getContext("2d");
          if (!context) continue;

          await page.render({ canvasContext: context, viewport }).promise;

          if (cancelled || !containerRef.current) return;
          containerRef.current.appendChild(canvas);
        }
      } catch (err) {
        console.error("Failed to render PDF", err);
        if (!cancelled) setError("Unable to load this PDF.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    renderPdf();

    return () => {
      cancelled = true;
    };
  }, [src]);

  return (
    <div className="gx-pdf-preview">
      {loading && <div className="gx-pdf-preview-loading">Loading PDF…</div>}
      {error && <div className="gx-pdf-preview-error">{error}</div>}
      <div ref={containerRef} className="gx-pdf-preview-pages" />
    </div>
  );
};

const FileCard: React.FC<FileCardProps> = ({
  fileTitle,
  fileName,
  fileUrl,
  base64Output,
  filePath,
  fileType = "auto",
  downloading = false,
  onDownload,
  onDelete,
  onRemove,
  statusContent,
  className = "",
}) => {
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const normalizedBase64 = normalizeSrc(base64Output);
  const resolvedSrc = fileUrl || filePath || normalizedBase64;
  const kind = getFileKind(fileName, fileType);

  // A real file source is required for both viewing and downloading —
  // no dummy/fallback file, and no button rendered without one.
  const hasSource = Boolean(resolvedSrc);
  const canView = hasSource;
  const canDownload = hasSource || Boolean(onDownload);
  const canDelete = Boolean(onDelete);
  const canRemove = Boolean(onRemove);

  const handleView = () => {
    if (!canView) return;
    setIsViewerOpen(true);
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
      return;
    }
    if (!hasSource || !resolvedSrc) return;
    const link = document.createElement("a");
    link.href = resolvedSrc;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div
      className={`flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-white shadow-sm ${className}`}
    >
      {/* Left section: file icon and details */}
      <div className="flex items-center space-x-3">
        <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-lg">
          <img src={`${ICON_BASE_PATH}fileview.svg`} alt="Fileview" />
        </div>
        <div>
          <p className="gx-p14-sb font-medium text-gray-900">{fileTitle}</p>
          <p className="gx-p10-sb text-gray-500">{fileName}</p>
        </div>
      </div>

      {/* Right section: view/download/delete/remove + custom status slot —
          each renders only when the data/handler it needs is actually
          present */}
      <div className="flex items-center space-x-2">
        {(canView || canDownload || canDelete || canRemove) && (
          <div className="gx-stmt-card-right flex items-center gap-3">
            {canView && (
              <GxIconButton
                type="view"
                onClick={handleView}
                alt="view"
                title={`View ${fileName}`}
                className="gx-stmt-view-icon"
              />
            )}

            {canDownload && (
              <GxIconButton
                type="download"
                onClick={handleDownload}
                disabled={downloading}
                alt="download"
                title={`Download ${fileName}`}
                loading={downloading}
                className="gx-profile-statement-download-icon"
              />
            )}

            {canDelete && (
              <GxIconButton
                type="delete"
                onClick={onDelete}
                alt="delete"
                title={`Delete ${fileName}`}
                className="gx-stmt-delete-icon"
              />
            )}

            {/* NEW: "x" remove button - separate from onDelete above, for
                clearing an already-on-file document so the consumer can
                swap back to its upload control (see onRemove usage in
                ThirdPartyConnectForm). Plain button rather than
                GxIconButton since its `type` prop only covers
                view/download/delete today. */}
            {canRemove && (
              <button
                type="button"
                onClick={onRemove}
                aria-label={`Remove ${fileName}`}
                title={`Remove ${fileName}`}
                className="gx-filecard-remove-btn"
              >
                ×
              </button>
            )}
          </div>
        )}

        {statusContent}
      </div>

      {canView && (
        <GxDialog
          isOpen={isViewerOpen}
          onClose={() => setIsViewerOpen(false)}
          title={fileTitle}
          subtitle={fileName}
          size="medium"
        >
          {kind === "image" ? (
            <div className="gx-image-preview">
              <img src={resolvedSrc} alt={fileName} className="gx-image-preview-img" />
            </div>
          ) : (
            resolvedSrc && <PdfPreview src={resolvedSrc} />
          )}
        </GxDialog>
      )}
    </div>
  );
};

export default FileCard;
